import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Journal d'activité des travailleurs plateforme.
//
// Enregistre « qui a fait quoi » dans le back-office (ajout / modification /
// suppression de travailleurs, changement de réglages PWA, connexions…) avec
// horodatage, puis permet de consulter cet historique.
//
// On réutilise la table `user_audit_log` avec la catégorie « platform » :
// `user_id` = auteur de l'action, et les détails (rôle, cible, gravité) sont
// rangés dans `metadata`. Tout est BEST-EFFORT.
// ─────────────────────────────────────────────────────────────

export type PlatformActivityLevel = 'info' | 'success' | 'warning' | 'danger';

export interface PlatformActivityInput {
  actorId: number | null | undefined;
  actorName?: string | null;
  actorRole?: string | null;
  /** Clé technique de l'action, ex : 'team.create', 'pwa.update'. */
  action: string;
  description: string;
  targetType?: string | null;
  targetId?: number | string | null;
  level?: PlatformActivityLevel;
}

export interface PlatformActivityEntry {
  id: number;
  actorId: number | null;
  userName: string;
  actorRole: string | null;
  action: string;
  description: string;
  targetType: string | null;
  targetId: string | null;
  level: string;
  createdAt: string | null;
}

/** Enregistre une action d'un travailleur plateforme (best-effort). */
export async function logPlatformActivity(input: PlatformActivityInput): Promise<void> {
  const actorId = Number(input.actorId);
  if (!Number.isFinite(actorId) || actorId <= 0) return;
  try {
    await supabase.from('user_audit_log').insert({
      user_id: actorId,
      category: 'platform',
      action: input.action,
      description: (input.description || '').slice(0, 400),
      actor_id: actorId,
      metadata: {
        actor_name: input.actorName ?? null,
        actor_role: input.actorRole ?? null,
        target_type: input.targetType ?? null,
        target_id: input.targetId != null ? String(input.targetId) : null,
        level: input.level ?? 'info',
      },
    });
  } catch {
    /* audit best-effort */
  }
}

/** Historique des actions plateforme, du plus récent au plus ancien. */
export async function listPlatformActivity(
  limit = 200,
  actorId?: number | null,
): Promise<PlatformActivityEntry[]> {
  try {
    let query = supabase
      .from('user_audit_log')
      .select('id, user_id, action, description, actor_id, metadata, created_at')
      .eq('category', 'platform')
      .order('created_at', { ascending: false })
      .limit(limit);
    if (actorId != null) query = query.eq('user_id', actorId);

    const { data, error } = await query;
    if (error || !data) return [];

    const rows = data as Record<string, unknown>[];
    const ids = Array.from(
      new Set(rows.map((r) => Number(r.user_id)).filter((n) => Number.isFinite(n) && n > 0)),
    );
    const names: Record<number, string> = {};
    if (ids.length > 0) {
      const { data: users } = await supabase
        .from('users')
        .select('id, name, user_name')
        .in('id', ids);
      (users || []).forEach((u) => {
        const r = u as { id: number; name: string | null; user_name: string | null };
        names[r.id] = r.name || r.user_name || `Utilisateur #${r.id}`;
      });
    }

    return rows.map((r) => {
      const meta = (r.metadata as Record<string, unknown>) || {};
      const uid = r.user_id == null ? null : Number(r.user_id);
      return {
        id: Number(r.id),
        actorId: uid,
        userName: (meta.actor_name as string) || (uid != null ? names[uid] : '') || `Utilisateur #${r.user_id}`,
        actorRole: (meta.actor_role as string) ?? null,
        action: String(r.action || ''),
        description: String(r.description || ''),
        targetType: (meta.target_type as string) ?? null,
        targetId: meta.target_id != null ? String(meta.target_id) : null,
        level: String(meta.level || 'info'),
        createdAt: r.created_at ? String(r.created_at) : null,
      };
    });
  } catch {
    return [];
  }
}

export const PLATFORM_ACTION_META: Record<string, { label: string; icon: string; className: string }> = {
  'team.create': { label: 'Travailleur ajouté', icon: 'ri-user-add-line', className: 'text-accent-700 bg-accent-100' },
  'team.update': { label: 'Accès modifiés', icon: 'ri-shield-user-line', className: 'text-primary-700 bg-primary-100' },
  'team.suspend': { label: 'Travailleur suspendu', icon: 'ri-user-unfollow-line', className: 'text-amber-700 bg-amber-100' },
  'team.activate': { label: 'Travailleur réactivé', icon: 'ri-user-follow-line', className: 'text-accent-700 bg-accent-100' },
  'team.remove': { label: 'Travailleur retiré', icon: 'ri-user-minus-line', className: 'text-red-700 bg-red-100' },
  'team.password': { label: 'Mot de passe réinitialisé', icon: 'ri-lock-password-line', className: 'text-secondary-700 bg-secondary-100' },
  'pwa.update': { label: 'Réglages PWA modifiés', icon: 'ri-smartphone-line', className: 'text-primary-700 bg-primary-100' },
  login: { label: 'Connexion back-office', icon: 'ri-login-box-line', className: 'text-foreground-700 bg-foreground-100' },
};

export function platformActionMeta(action: string) {
  return (
    PLATFORM_ACTION_META[action] || {
      label: action,
      icon: 'ri-flashlight-line',
      className: 'text-foreground-600 bg-background-100',
    }
  );
}

export function platformTimeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
}